import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useBusinessDetailsQuery } from './useBusinessDetails';
import { apiService } from '../services/api';
import { queryKeys } from '../lib/queryKeys';
import { useAuthContext } from '../contexts/AuthContext';

/**
 * Whether a section's own source has actually answered. `sectionsCompleted` says
 * `false` both while a request is in flight and when it failed, which is fine for
 * a sidebar dot but misleading anywhere that asks the owner to act on it (AB#2108).
 */
export type SectionLoadState = 'ready' | 'checking' | 'failed';

function loadState(query: { isSuccess: boolean; isError: boolean }): SectionLoadState {
    if (query.isError) return 'failed';
    return query.isSuccess ? 'ready' : 'checking';
}

/**
 * AB#1675 — Onboarding shell.
 *
 * Derives whether the salon owner's business setup is "still incomplete"
 * from the live `GET /business/my-business` response. Used by:
 *   - Sidebar "My Business" item to show the red-dot indicator
 *   - Profile menu "My Business" item to show the SET UP highlight
 *   - Per-section sub-nav status dots inside /my-business
 *
 * Phase A — only `businessDetails` had a real source.
 * Phase B (this commit) — `branches` is now wired to a TanStack Query
 * against `/salon-owner/branches`, gated on a business existing so we don't
 * spam the BE with 401/404 for owners who haven't created one yet.
 *
 * Other section flags (services, staff, …) stay `false` until their
 * matching endpoints exist or a single
 * `GET /api/v2/onboarding/progress` summary endpoint lands.
 *
 * `businessDetails` is considered complete when the BE record has a name,
 * an about blurb, a country, AND at least one open day in business hours.
 * `branches` is considered complete when ≥1 active branch exists. We keep
 * the bar deliberately low so the dot turns green on meaningful setup —
 * fine-grained validation lives in each form, not in the gating UI.
 */
export function useBusinessSetupStatus() {
    const detailsQuery = useBusinessDetailsQuery();
    const { data, isLoading } = detailsQuery;

    // Branches list — only fire once a business profile actually exists,
    // otherwise the BusinessAuthorize attribute on /salon-owner/branches
    // would 401 every render of the sidebar.
    const hasBusiness = !!data?.id;
    // AB#2175 (Batch A) — the branches and services lists are owner-only endpoints; the
    // setup dots are an owner concern, so staff sessions never fire them.
    const { isSalonOwner } = useAuthContext();
    const branchQuery = useQuery({
        queryKey: queryKeys.branches.list(),
        queryFn: () => apiService.getBranches(),
        enabled: hasBusiness && isSalonOwner,
        staleTime: 60_000,
        retry: 1,
    });

    // CR-2026-05-12 — Services list — same gating as Branches above.
    // Lights the sidebar dot green when ≥1 active service is published,
    // which mirrors the activation leading indicator (≥3 services threshold
    // is surfaced inside the section itself, not on the sidebar).
    const serviceQuery = useQuery({
        queryKey: queryKeys.services.list(),
        queryFn: () => apiService.getOwnerServices(),
        enabled: hasBusiness && isSalonOwner,
        staleTime: 60_000,
        retry: 1,
    });

    // CR-2026-05-15 — Subscribed master categories. Lights the sidebar dot
    // green when the business is subscribed to ≥1 active category. This sits
    // between Branches and Services in the onboarding mental model — a
    // service can't exist without a parent category.
    const categoryQuery = useQuery({
        queryKey: queryKeys.categories.business(),
        queryFn: () => apiService.getBusinessCategories(),
        enabled: hasBusiness,
        staleTime: 60_000,
        retry: 1,
    });

    // While loading we treat the state as incomplete so the red-dot stays
    // visible — better than flashing it off then on.
    const businessDetailsCompleted = (() => {
        if (isLoading || !data) return false;
        const hasName = typeof data.name === 'string' && data.name.trim().length >= 2;
        const hasAbout = typeof data.about === 'string' && data.about.trim().length >= 1;
        const location = (data.location ?? {}) as { country?: string };
        const hasCountry = typeof location.country === 'string' && location.country.length > 0;
        const hours = data.businessHours ?? [];
        const hasAnyOpenDay = hours.some(h => {
            const r = h as { isOpen?: boolean };
            return r.isOpen === true;
        });
        return hasName && hasAbout && hasCountry && hasAnyOpenDay;
    })();

    const branchesCompleted = !!branchQuery.data?.branches?.some(br => br.active);

    // CR-2026-05-12 — Services dot lights green when ≥1 active service exists.
    // Same low-bar policy as Branches; rich validation (≥3 services for
    // marketplace eligibility) is surfaced inside the section itself.
    const servicesCompleted = !!serviceQuery.data?.services?.some(s => s.isActive);

    // CR-2026-05-15 — Categories dot lights green when ≥1 active subscribed
    // category exists. Backed by GET /category/business (already auth-gated
    // server-side).
    const categoriesCompleted = !!categoryQuery.data?.categories?.some(c => c.isActive);

    const sectionsCompleted = {
        businessDetails: businessDetailsCompleted,
        branches: branchesCompleted,
        categories: categoriesCompleted,
        services: servicesCompleted,
        // Phase B+ — these still light up red until the matching endpoints exist.
        bookingSettings: false,
        staff: false,
        roles: false,
        bankDetails: false,
        plan: false,
        tax: false,
    } as const;

    const isIncomplete = Object.values(sectionsCompleted).some(v => !v);

    // AB#2108 — the same four sources, reported as "has this actually answered?".
    // Anything that asks the owner to act on a section (the Submit to Devanza
    // checklist) must tell "not done" apart from "not known yet" / "failed";
    // `isIncomplete` and `sectionsCompleted` keep their original meaning for the
    // sidebar and profile-menu indicators.
    // Branches / categories / services only run once my-business reports a business.
    // `getMyBusiness` turns a 404 or empty body into `null` WITHOUT throwing (a real
    // transient right after creation), which would leave those three gated off and
    // reporting "checking" forever. Report that as "cannot check" instead, so callers
    // offer a retry rather than an unexplained wait.
    const dependentsBlocked = detailsQuery.isSuccess && !hasBusiness;
    const dependentState = (query: { isSuccess: boolean; isError: boolean }): SectionLoadState =>
        dependentsBlocked ? 'failed' : loadState(query);

    const sectionsState = {
        businessDetails: loadState(detailsQuery),
        branches: dependentState(branchQuery),
        categories: dependentState(categoryQuery),
        services: dependentState(serviceQuery),
    } as const;

    /** Refetch only what could not be read, so a transient failure needs no page reload. */
    const retryFailedSections = useCallback(() => {
        if (detailsQuery.isError || dependentsBlocked) void detailsQuery.refetch();
        if (branchQuery.isError) void branchQuery.refetch();
        if (categoryQuery.isError) void categoryQuery.refetch();
        if (serviceQuery.isError) void serviceQuery.refetch();
    }, [detailsQuery, branchQuery, categoryQuery, serviceQuery, dependentsBlocked]);

    return { isIncomplete, sectionsCompleted, sectionsState, retryFailedSections } as const;
}

export type SectionKey = keyof ReturnType<typeof useBusinessSetupStatus>['sectionsCompleted'];
